import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useApiGet, getApiErrorMessage } from '../../utils/httpClientTs';
import {
  downloadPayoutDepositReport,
  getPayoutDetail,
} from '../../endpoints/financePayments';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const PaymentsPayoutDetailPage: React.FC = () => {
  const { payoutId } = useParams<{ payoutId: string }>();
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useApiGet(
    ['finance-payments-payout', payoutId],
    () => getPayoutDetail(payoutId!),
    { enabled: !!payoutId, retry: 1 }
  );

  const p = data?.payout;

  const handleDownload = async () => {
    if (!payoutId) return;
    try {
      setErrorMsg(null);
      setDownloading(true);
      await downloadPayoutDepositReport(payoutId);
    } catch (e) {
      setErrorMsg(getApiErrorMessage(e));
    } finally {
      setDownloading(false);
    }
  };

  if (!payoutId) {
    return <Typography>Missing payout id.</Typography>;
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/finance/payments/payouts')}
        sx={{ mb: 2 }}
      >
        Back to payouts
      </Button>

      {errorMsg && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMsg(null)}
        >
          {errorMsg}
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={22} />
          <Typography>Loading…</Typography>
        </Box>
      )}

      {p && (
        <>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {p.stripe_payout_id}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Status: <strong>{p.status}</strong> · Amount: {fmtMoney(p.amount)}{' '}
            {p.currency ? p.currency.toUpperCase() : ''} · Balance lines:{' '}
            {p.balance_line_count}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Arrival:{' '}
            {p.arrival_date ? new Date(p.arrival_date).toLocaleString() : '—'} ·
            Synced: {p.synced_at ? new Date(p.synced_at).toLocaleString() : '—'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            disabled={downloading}
            onClick={() => void handleDownload()}
          >
            Download deposit / GL report (PDF)
          </Button>
        </>
      )}
    </Box>
  );
};

export default PaymentsPayoutDetailPage;
